import React from 'react';

import { Coordinates, StoreAddress, SupportedCountry } from '@flipdish/api-client-typescript/api';
import { Card, CardContent, Hidden, Typography } from '@material-ui/core';
import { createStyles, Theme, WithStyles, withStyles } from '@material-ui/core/styles';
import withWidth, { WithWidth } from '@material-ui/core/withWidth';
import classnames from 'classnames';
import { FormikProps, withFormik } from 'formik';
import Cookies from 'js-cookie';
import { debounce, union, without } from 'lodash';
import { withSnackbar, withSnackbarProps } from 'notistack';
import { GoogleMap, Marker, withGoogleMap, withScriptjs } from 'react-google-maps';
import StandaloneSearchBox from 'react-google-maps/lib/components/places/StandaloneSearchBox';
import { getTranslate, Translate } from 'react-localize-redux';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { compose, setDisplayName, UnpackICEWP, withProps, withState } from 'recompose';

import { accountActions } from '../../../actions/account.actions';
import { storeActions, updateAddress, updateCoordinates } from '../../../actions/store.actions';
import markerSvg from '../../../assets/images/marker.svg';
import { DEFAULT_MAP_CENTER, GMAP_API_KEY } from '../../../constants/map.constant';
import { signup } from '../../../constants/signup.constants';
import { parseAddressFromResult } from '../../../helpers/geoCodeResultParser';
import {
  applyLatLngOffset,
  getLatLngOffset,
  getLatLngOfPositionOnMap,
} from '../../../helpers/maps';
import { getStores } from '../../../selectors/store.selector';
import {
  CurrentLocationResult,
  getCurrentLocation,
} from '../../../services/geolocation/browserGeolocation';
import ConfirmationForm from './ConfirmationForm';
import { scriptContents } from './FacebookTracker';
import LocatorForm from './LocatorForm';
import { mapsStyles } from './MapsStyles';
import { StepsHeader } from './StepsHeader';

const stepIndex = signup.steps.indexOf('Store_location');

const styles = (theme: Theme) =>
  createStyles({
    overlay: {
      [theme.breakpoints.up('sm')]: {
        position: 'absolute',
        zIndex: 5,
        display: 'flex',
        justifyContent: 'flex-start',
        flex: 1,
        right: theme.spacing(8),
        bottom: theme.spacing(8),
      },
    },
    card: {
      background: '#fafafa',
      transition: 'all 0.3s ease',
      padding: theme.spacing(2),
      overflow: 'visible',
      [theme.breakpoints.up('sm')]: {
        width: '584px',
      },
    },
    form: {
      [theme.breakpoints.up('xs')]: {
        maxWidth: '520px',
      },
    },
    mapContainer: {
      display: 'flex',
      flexDirection: 'column',
      flexWrap: 'nowrap',
      width: '100%',
      height: '100vh',
    },
    mapElement: {
      position: 'relative',
      flex: '1 1 auto',
      width: '100%',
      minHeight: 132,
      height: '51.875vh',
    },
    mapElementSmall: {
      [theme.breakpoints.down('xs')]: {
        height: '32.8vh',
      },
    },
    mapElementMask: {
      '&:after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
      },
    },
    buttonPrimary: {
      backgroundColor: '#ff0046',
      color: '#fff',
      '&:hover': {
        backgroundColor: '#b20031',
      },
      '&:active': {
        backgroundColor: '#b20031',
      },
      letterSpacing: '1.4px',
      [theme.breakpoints.down('md')]: {
        letterSpacing: '1.3px',
      },
    },
    buttonDefault: {
      [theme.breakpoints.up('sm')]: {
        minHeight: '48px',
      },
    },
    stepsShort: {
      color: 'rgba(0, 0, 0, 0.38)',
      marginBottom: 6,
    },
  });

interface IFormValues {
  addressFormatted: string;
  addressLine1: string;
  area: string;
  addressCountryCode: string;
  addressCity: string;
  administrativeAreaLevel1: string;
  postCode: string;
  coordinates: { lat: number; lng: number };
}

type IsFormState = UnpackICEWP<typeof useIsFormState>;
const useIsFormState = withState('isForm', 'setIsForm', false);

type MappedState = ReturnType<ReturnType<typeof mapStateToPropsFactory>>;
const mapStateToPropsFactory = (initialState, ownProps) => {
  return (state: AppState, ownProps) => {
    const { account } = state;
    const stores = getStores(state);
    const store = stores.length ? stores[0] : undefined;
    const supportedCountries = account.supportedCountries;

    return {
      account,
      store,
      supportedCountries,
      translate: getTranslate(state.locale),
    };
  };
};

type MappedDispatch = ReturnType<ReturnType<typeof mapDispatchToPropsFactory>>;
const mapDispatchToPropsFactory = (dispatch, ownProps) => {
  return (dispatch: ThunkDispatch, ownProps) => {
    return {
      getAccount: () => {
        dispatch(accountActions.getAccountDetails());
      },
      updateCoordinates: (
        storeId: number,
        storeGroupId: number,
        coordinates: Required<Coordinates>
      ) => {
        dispatch(updateCoordinates(storeId, storeGroupId, coordinates));
      },
      loadStores: () => {
        dispatch(storeActions.loadAll());
      },
      getSupportedCountriesList: () => {
        dispatch(accountActions.getSupportedCountries());
      },
      save: async (
        storeId: number,
        storeGroupId: number,
        address: StoreAddress,
        coordinates: Required<Coordinates>
      ) => {
        dispatch(
          accountActions.setTimeZoneBasedOnLatLng(coordinates.Latitude, coordinates.Longitude)
        );
        await dispatch(updateAddress(storeId, storeGroupId, address, coordinates));
      },
    };
  };
};

type initState = {
  addressEntered: boolean;
  center: { lat: number; lng: number };
  confirmFields: boolean;
  countryCode: string;
  googleObjectAddress: {
    Results: any;
    Status: any;
  };
  mapInitialized: boolean;
  supportedCountry: SupportedCountry | null;
  timeoutIds: number[];
};

const initState: initState = {
  addressEntered: false,
  center: DEFAULT_MAP_CENTER,
  confirmFields: false,
  countryCode: '',
  googleObjectAddress: {
    Results: [],
    Status: ('OK' as unknown) as google.maps.GeocoderStatus,
  },
  mapInitialized: false,
  supportedCountry: {} as SupportedCountry,
  timeoutIds: [] as number[],
};

type Props = WithStyles<typeof styles> &
  RouteComponentProps &
  withSnackbarProps &
  WithWidth &
  IsFormState &
  MappedState &
  MappedDispatch &
  FormikProps<IFormValues>;
const EnhancedComponent = compose(
  setDisplayName('StoreLocation'),
  withRouter,
  withStyles(styles, {
    name: 'StoreLocation',
    withTheme: true,
  }),
  withSnackbar,
  withWidth(),
  useIsFormState,
  withProps(({ width, isForm, classes }) => {
    return {
      googleMapURL:
        'https://maps.googleapis.com/maps/api/js?v=3.exp&libraries=geometry,drawing,places&key=' +
        GMAP_API_KEY,
      loadingElement: <div className="loading" />,
      containerElement: <div className={classes.mapContainer} />,
      mapElement: (
        <div
          className={classnames(classes.mapElement, {
            [classes.mapElementSmall]: isForm,
            [classes.mapElementMask]: isForm,
          })}
        />
      ),
    };
  }),
  withScriptjs,
  withGoogleMap,
  connect(mapStateToPropsFactory, mapDispatchToPropsFactory),
  withFormik<Props, IFormValues>({
    displayName: 'StoreLocation',
    mapPropsToValues: (props) => {
      return {
        addressFormatted: '',
        addressLine1: '',
        area: '',
        addressCountryCode: '',
        addressCity: '',
        administrativeAreaLevel1: '',
        postCode: '',
        coordinates: { ...DEFAULT_MAP_CENTER },
      };
    },
    handleSubmit: async (values, { props, setSubmitting, setFieldError }) => {
      try {
        const { store, save, getAccount } = props;
        const storeAddress: StoreAddress = {
          Line1: values.addressLine1,
          City: values.addressCity,
          Postcode: values.postCode,
          CountryCode: values.addressCountryCode,
        };
        const storeCoordinates: Required<Coordinates> = {
          Latitude: values.coordinates.lat,
          Longitude: values.coordinates.lng,
        };

        if (!store) {
          props.enqueueSnackbar(props.translate('Store_is_required!'), { variant: 'error' });
          throw new Error('Store is required!');
        }

        await save(
          store.StoreId as number,
          store.StoreGroupId as number,
          storeAddress,
          storeCoordinates
        );
        // get new setup steps
        await getAccount();
      } catch (error) {
        // setFieldError('', props.translate('') as string);
        // props.enqueueSnackbar(props.translate(''), { variant: 'error' });
        setSubmitting(false);
        console.error(error);
      }
    },
    validate: (values, props) => {
      const errors: { [P in keyof IFormValues]?: string } = {};
      if (!values.addressLine1) {
        errors.addressLine1 = props.translate('Required') as string;
      }
      return errors;
    },
  })
)(
  class StoreLocation extends React.Component<Props, typeof initState> {
    private searchBoxRef = React.createRef<StandaloneSearchBox>();
    private mapRef = React.createRef<GoogleMap>();
    private geocode?: ReturnType<typeof debounce>;
    private markerLandscape = { x: 0.3, y: 0.4 };
    private markerPortrait = { x: 0.5, y: 0.35 };
    private markerCentered = { x: 0.5, y: 0.5 };

    public constructor(props) {
      super(props);
      this.state = {
        ...initState,
      };
    }

    public componentDidMount() {
      function FacebookPixelTracker(advertisingConsent) {
        const scriptEl = document.createElement('script');
        const scriptText = document.createTextNode(scriptContents(advertisingConsent));
        scriptEl.appendChild(scriptText);
        document.head.appendChild(scriptEl);
      }

      process.env.NODE_ENV === 'production' &&
        !Cookies.get('_fbp') &&
        FacebookPixelTracker(this.props?.account?.gdpr?.advertising);

      this.props.loadStores();
      this.focus();

      getCurrentLocation()
        .then(this.handleGetCurrentLocation)
        .catch((err) => console.error(err));

      this.props.getSupportedCountriesList();
    }

    public componentWillUnmount() {
      this.state.timeoutIds.forEach((id) => clearTimeout(id));
    }

    private renderGoogleMap = () => {
      return (
        <GoogleMap
          defaultZoom={16}
          ref={this.mapRef}
          onDragEnd={this.onDragEnd}
          onDrag={this.onDrag}
          onZoomChanged={this.onZoom}
          center={this.state.center}
          onIdle={this.onMapIdle}
          defaultOptions={{
            streetViewControl: false,
            scaleControl: false,
            mapTypeControl: false,
            panControl: false,
            rotateControl: false,
            fullscreenControl: false,
            zoomControl: false,
            styles: mapsStyles,
          }}
        >
          <Marker
            position={this.props.values.coordinates}
            icon={markerSvg}
            draggable={true}
            onDrag={this.onMarkerDrag}
            onDragEnd={this.onMarkerDragEnd}
          />
        </GoogleMap>
      );
    };

    public render() {
      const {
        classes,
        translate,
        isValid,
        isSubmitting,
        touched,
        errors,
        handleBlur,
        handleChange,
        values,
        store,
      } = this.props;
      const storeName = store ? store.Name : '';

      return (
        <>
          {this.renderGoogleMap()}
          <div>
            <Hidden xsDown>
              <StepsHeader steps={signup.steps} stepIndex={stepIndex} />
            </Hidden>
            <div className={classes.overlay}>
              <Card className={classnames(classes.card)}>
                <CardContent>
                  <Hidden smUp>
                    <Typography variant="caption" className={classes.stepsShort}>
                      <Translate>
                        {(translate) =>
                          `${translate('Step')} ${stepIndex + 1}/${signup.steps.length}`
                        }
                      </Translate>
                    </Typography>
                  </Hidden>
                  <Typography variant="h4">
                    {!this.state.confirmFields ? (
                      <Translate id="Where_is_your_store_located" />
                    ) : (
                      <Translate id="Please_verify_the_address" />
                    )}
                  </Typography>
                  <form
                    onReset={this.props.handleReset}
                    onSubmit={this.handleSubmit}
                    className={classes.form}
                  >
                    {this.state.confirmFields ? (
                      <ConfirmationForm
                        errors={errors}
                        getAccount={this.props.getAccount}
                        goBack={this.goBack}
                        handleBlur={handleBlur}
                        handleChange={handleChange}
                        coordinates={{
                          Latitude: this.props.values.coordinates.lat,
                          Longitude: this.props.values.coordinates.lng,
                        }}
                        isSubmitting={isSubmitting}
                        isValid={isValid}
                        storeGroupId={this.props.store.StoreGroupId}
                        storeId={this.props.store.StoreId}
                        storeName={storeName}
                        supportedCountry={this.state.supportedCountry}
                        touched={touched}
                        translate={translate}
                        value={values}
                      />
                    ) : (
                      <LocatorForm
                        addressEntered={this.state.addressEntered}
                        errors={errors}
                        googleObjectAddress={this.state.googleObjectAddress}
                        handleBlur={handleBlur}
                        handleChange={handleChange}
                        isSubmitting={isSubmitting}
                        isValid={isValid}
                        onSearchBoxPlaceChange={this.onSearchBoxPlaceChange}
                        searchBoxRef={this.searchBoxRef}
                        setFieldValue={this.props.setFieldValue}
                        setStepToConfirmLocation={this.setStepToConfirmLocation}
                        storeName={storeName}
                        supportedCountry={this.state.supportedCountry}
                        touched={touched}
                        translate={translate}
                        values={values}
                      />
                    )}
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </>
      );
    }

    public focus = () => {
      // on newer android chrome this is annoying... focusing on every map drag
      // if (this.inputRef.current) {
      //   this.inputRef.current.focus();
      // }
    };

    private onMapIdle = () => {
      if (this.state.mapInitialized) {
        return;
      }
      this.setState({ mapInitialized: true });
    };

    private handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (!this.props.isValid) {
        return;
      }

      if (this.state.confirmFields) {
        this.props.handleSubmit(e);
      } else {
        this.setStepToConfirmLocation();
      }
    };

    private onDragEnd = () => {
      const map = this.mapRef.current;
      if (map === null) {
        return;
      }

      const newMarkerLocation = this.getLatLngForMarkerScreenPosition().toJSON();
      const newCenter = map.getCenter().toJSON();

      this.setState({
        center: newCenter,
      });
      this.props.setFieldValue('coordinates', newMarkerLocation);

      this.geocodeLocation(newMarkerLocation);
    };

    private onDrag = () => {
      this.props.setSubmitting(true);

      this.props.setFieldValue('coordinates', this.getLatLngForMarkerScreenPosition().toJSON());
    };

    private onZoom = () => {
      const map = this.mapRef.current;
      if (map === null) {
        return;
      }
      const markerLatLng = new google.maps.LatLng(
        this.props.values.coordinates.lat,
        this.props.values.coordinates.lng
      );
      const newCenter = this.translateFromMarkerToCenter(markerLatLng);
      map.panTo(newCenter);
      this.setState({ center: newCenter.toJSON() });
    };

    private onMarkerDrag = () => {
      this.props.setSubmitting(true);
    };
    private onMarkerDragEnd = (e: google.maps.MouseEvent) => {
      // this.props.setSubmitting(true)
      this.moveMapToNewLocationAndGeocode(e.latLng.toJSON());
    };

    private onSearchBoxPlaceChange = () => {
      if (!this.searchBoxRef.current) {
        return;
      }

      const places = this.searchBoxRef.current.getPlaces();

      if (places && places.length > 0) {
        this.handlePlacesChanged(places, true);
      }
    };

    private onMapMovePlaceChange = (
      placesArg: google.maps.places.PlaceResult[],
      status: google.maps.GeocoderStatus
    ) => {
      if (status === google.maps.GeocoderStatus.OK) {
        this.handlePlacesChanged(placesArg, false);
      }
      this.props.setSubmitting(false);
    };

    private handlePlacesChanged = (
      placesArg: google.maps.places.PlaceResult[],
      panToLocation: boolean
    ) => {
      const place = placesArg[0];

      if (!place || !this.placeIsValid(place)) {
        console.log('Address invalid, unable to proceed.');
        return;
      }
      this.setState({
        googleObjectAddress: {
          Results: place,
          Status: 'OK' as string,
        },
      });

      const addressInfo = parseAddressFromResult(place, this.props.supportedCountries);
      const countryCode = addressInfo && addressInfo.countryCode;
      const supportedCountry = addressInfo && addressInfo.supportedCountry;
      this.setState({ ...this.state, countryCode, supportedCountry });

      if (addressInfo == null) {
        return;
      }

      const updatedPositions = this.getUpdatedPositionsForPlace(
        place.geometry.location,
        panToLocation
      );
      const updatedState = this.buildUpdatedState(addressInfo, {
        center: updatedPositions.center,
      });

      this.props.setValues({
        addressFormatted: addressInfo.fullAddress,
        addressLine1: addressInfo.addressLine1,
        area: addressInfo.area,
        addressCity: addressInfo.city,
        addressCountryCode: addressInfo.countryCode,
        postCode: addressInfo.postCode,
        administrativeAreaLevel1: addressInfo.administrativeAreaLevel1,
        coordinates: updatedPositions.marker || this.props.values.coordinates,
      });
      this.setState(updatedState);
    };

    private geocodeLocation = (location: google.maps.LatLngLiteral) => {
      if (this.geocode) {
        this.geocode.cancel();
      }
      const geocoder = new google.maps.Geocoder();
      this.geocode = debounce(geocoder.geocode, 1000);

      this.geocode({ location }, this.onMapMovePlaceChange);
    };

    private goBack = () => {
      this.setConfirmState(false);
    };

    private setStepToConfirmLocation = () => {
      this.setConfirmState(true);
    };

    private setConfirmState(confirm: boolean) {
      this.setState({ confirmFields: confirm });
      this.props.setIsForm(confirm);
    }

    private placeIsValid(place) {
      return place.address_components && place.formatted_address && place.geometry;
    }

    private buildUpdatedState(addressInfo, updatedPositions) {
      return {
        addressEntered: true,
        // addressFormatted: addressInfo.fullAddress,
        // addressLine1: addressInfo.addressLine1,
        // addressLine2: addressInfo.addressLine2,
        // addressCity: addressInfo.city,
        // addressCountryCode: addressInfo.countryCode,
        ...updatedPositions,
      };
    }

    private moveMapToNewLocationAndGeocode(markerLocation: google.maps.LatLngLiteral) {
      const map = this.mapRef.current;
      if (map === null) {
        return;
      }
      const newCenter = this.translateFromMarkerToCenter(
        new google.maps.LatLng(markerLocation.lat, markerLocation.lng)
      );

      map.panTo(newCenter);
      const timeoutId = window.setTimeout(() => {
        this.props.setFieldValue('coordinates', markerLocation);
        this.setState(
          {
            center: newCenter.toJSON(),
            // marker: markerLocation.toJSON(),
            timeoutIds: without(this.state.timeoutIds, timeoutId),
          },
          () => this.geocodeLocation(this.props.values.coordinates)
        );
      }, 500);
      this.setState({ timeoutIds: union(this.state.timeoutIds, [timeoutId]) });
    }

    private getUpdatedPositionsForPlace(location: google.maps.LatLng, panToLocation) {
      const newCenter = this.translateFromMarkerToCenter(location);
      return {
        center: panToLocation ? newCenter.toJSON() : this.state.center,
        marker: panToLocation ? location.toJSON() : undefined,
      };
    }

    private translateFromMarkerToCenter(newMarker): google.maps.LatLng {
      const map = this.mapRef.current!;
      const offset = getLatLngOffset(this.getLatLngForMarkerScreenPosition(), map.getCenter(), map);
      return applyLatLngOffset(newMarker, offset, this.mapRef.current);
    }

    private getLatLngForMarkerScreenPosition() {
      const markerPosition = this.getMarkerPosition();
      return getLatLngOfPositionOnMap(markerPosition, this.mapRef.current);
    }

    private getMarkerPosition() {
      if (window.matchMedia('(max-width: 600px)').matches) {
        return this.markerCentered;
      }

      if (window.matchMedia('(orientation: portrait)').matches) {
        return this.markerPortrait;
      }

      if (window.matchMedia('(orientation: landscape)').matches) {
        return this.markerLandscape;
      }
      return this.markerCentered;
    }

    private isDefaultMapCenter = () => {
      return (
        this.state.center.lat === DEFAULT_MAP_CENTER.lat &&
        this.state.center.lng === DEFAULT_MAP_CENTER.lng
      );
    };
    private handleGetCurrentLocation = (coords: CurrentLocationResult) => {
      if (this.isDefaultMapCenter()) {
        const latLng = {
          lat: coords.latitude,
          lng: coords.longitude,
        };
        this.moveMapToNewLocationAndGeocode(latLng);
        this.props.setFieldValue('coordinates', latLng);
      }
    };
  }
);

export { EnhancedComponent as StoreLocation };
